import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { formatDateOnly } from '../utils/dateUtils'

// Función que obtiene datos reales de Meta Business
const fetchCampaignsData = async () => {
  console.log('🚀 Iniciando llamada a Meta Business API...')

  try {
    const response = await fetch('http://localhost:3001/api/meta-business/campaigns')
    
    if (!response.ok) {
      throw new Error(`Error Meta Business: ${response.status}`)
    }

    const metaData = await response.json()
    console.log('✅ Datos Meta Business obtenidos:', metaData)
    
    const metaCampaigns = metaData.data || []
    console.log('🔍 Meta campaigns array:', metaCampaigns.length, 'campañas')
    
    // Convertir formato Meta a formato del dashboard
    const formattedMetaCampaigns = metaCampaigns.map(campaign => ({
      campaign_id: campaign.campaign_id,
      campaign_name: campaign.campaign_name,
      account_name: campaign.account_name,
      account_id: campaign.account_id,
      client_name: campaign.client_name,
      platform: campaign.platform, // Ya viene como 'Meta'
      status: campaign.active ? 'ACTIVE' : 'PAUSED',
      active: campaign.active,
      // Métricas de insights (ya vienen como strings, convertir a números)
      impressions: parseInt(campaign.impressions) || 0,
      clicks: parseInt(campaign.clicks) || 0,
      reach: parseInt(campaign.reach) || 0,
      frequency: parseFloat(campaign.frequency) || 0,
      ctr: parseFloat(campaign.ctr) || 0,
      cpm: parseFloat(campaign.cpm) || 0,
      cpc: parseFloat(campaign.cpc) || 0,
      spend: parseFloat(campaign.spend) || 0,
      amount_spent: parseFloat(campaign.spend) || 0, // Alias para compatibilidad
      // Datos de campaña
      objective: campaign.objective,
      daily_budget: parseInt(campaign.daily_budget) || 0,
      lifetime_budget: parseInt(campaign.lifetime_budget) || 0,
      budget: parseInt(campaign.daily_budget) || 0, // Alias para compatibilidad
      // Fechas
      created_time: campaign.created_time,
      updated_time: campaign.updated_time,
      date_start: campaign.date_start,
      date_stop: campaign.date_stop,
      last_updated: campaign.updated_time || new Date().toISOString(),
      // Cálculos adicionales
      cost_micros: (parseFloat(campaign.spend) || 0) * 1000000,
      cost_per_result: campaign.clicks > 0 ? (parseFloat(campaign.spend) / parseInt(campaign.clicks)) : 0
    }))

    console.log(`✅ Datos procesados: ${formattedMetaCampaigns.length} campañas Meta`)
    return {
      metrics: formattedMetaCampaigns
    }

  } catch (error) {
    console.error('❌ Error obteniendo datos Meta Business:', error)
    // Retornar estructura vacía en caso de error
    console.log('📋 Retornando estructura vacía')
    return {
      metrics: []
    }
  }
}

// Función para recargar datos (usar misma lógica que fetchCampaignsData)
const refreshCampaignsData = async () => {
  console.log('🔄 Refrescando datos desde APIs...')
  return await fetchCampaignsData()
}

export const TYPE_OPTIONS = {
  ALL: 'all',
  GOOGLE: 'google',
  META: 'meta'
}

export const FILTER_OPTIONS = [
  { value: TYPE_OPTIONS.ALL, label: 'Todas las cuentas' },
  { value: TYPE_OPTIONS.GOOGLE, label: 'Google Ads' },
  { value: TYPE_OPTIONS.META, label: 'Meta Ads' }
]

const CampaignsContext = createContext()

export function CampaignsProvider({ children }) {
  // 🔥 ESTADOS PARA MANEJO DE API
  const [loading, setLoading] = useState(true) // Inicia en true para mostrar loading inicial
  const [dashboardData, setDashboardData] = useState(null) // Datos de la API
  const [error, setError] = useState(null) // Manejo de errores

  // Estados de UI
  const [selectedPlatform, setSelectedPlatform] = useState('all')
  const [selectedAccount, setSelectedAccount] = useState(null)

  // 🔥 EFECTOS PARA CARGA INICIAL DE DATOS
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true)
        setError(null)
        console.log('📡 Cargando datos iniciales...')

        const campaigns = await fetchCampaignsData()
        console.log('🔍 Campaigns data received:', campaigns)
        setDashboardData(campaigns)
        console.log('✅ Datos iniciales cargados correctamente')
      } catch (err) {
        console.error('❌ Error cargando datos iniciales:', err.message)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadInitialData()
  }, [])

  // Función para filtrar métricas por plataforma - usando datos de la API
  const getFilteredMetrics = useCallback(
    (platform) => {
      console.log('🔍 Filtering metrics for platform:', platform, 'dashboardData:', dashboardData)
      
      if (!dashboardData?.metrics) {
        console.log('⚠️ No metrics found in dashboardData')
        return []
      }

      if (platform === 'all') {
        console.log('✅ Returning all metrics:', dashboardData.metrics.length)
        return dashboardData.metrics
      }

      const filtered = dashboardData.metrics.filter((metric) =>
        platform === TYPE_OPTIONS.GOOGLE
          ? metric.platform.toLowerCase() === TYPE_OPTIONS.GOOGLE
          : metric.platform.toLowerCase() === TYPE_OPTIONS.META
      )
      
      console.log(`✅ Filtered metrics for ${platform}:`, filtered.length)
      return filtered
    },
    [dashboardData]
  ) // Depende de dashboardData

  // 🔥 FUNCIÓN PARA REFRESCAR DATOS
  const refreshData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔄 Refrescando datos...')

      const campaigns = await refreshCampaignsData()
      setDashboardData(campaigns)
      console.log('✅ Datos refrescados correctamente')
    } catch (err) {
      console.error('❌ Error refrescando datos:', err.message)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  // Función para cambiar la plataforma seleccionada
  const selectPlatform = useCallback((platform) => {
    setSelectedPlatform(platform)
  }, [])

  // Función para seleccionar una cuenta - usando datos de la API
  const selectAccount = useCallback(
    (accountId) => {
      const campaigns = dashboardData?.metrics?.filter((acc) => acc.account_id === accountId)
      const { account_name, client_name, platform } = campaigns[0] || {}

      setSelectedAccount({
        account_name,
        client_name,
        platform,
        campaigns
      })
    },
    [dashboardData]
  ) // Depende de dashboardData

  // Helpers para formateo optimizados con useMemo
  const getPlataformColor = useMemo(() => {
    return (platform) => {
      const platformColors = {
        Meta: 'bg-blue-100 text-blue-800',
        Google: 'bg-green-100 text-green-800'
      }
      return platformColors[platform] || 'bg-gray-100 text-gray-800'
    }
  }, [])

  const formatCurrency = useMemo(() => {
    return (amount) => {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
    }
  }, [])

  const formatNumber = useMemo(() => {
    return (num) => {
      return new Intl.NumberFormat('es-MX').format(num)
    }
  }, [])

  // 🔥 OBJETO VALUE OPTIMIZADO CON USEMEMO - INCLUYE MANEJO DE API
  const value = useMemo(
    () => ({
      // Data from API
      dashboardData, // Datos completos de la API
      campaigns: getFilteredMetrics(selectedPlatform), // Campañas filtradas

      // API Status
      loading, // Estado de carga
      error, // Errores de la API

      // UI State
      selectedPlatform,
      selectedAccount,

      // Actions
      selectPlatform,
      selectAccount,
      refreshData, // 🆕 Función para refrescar datos

      // Helpers (optimizados)
      getPlataformColor,
      formatCurrency,
      formatNumber,
      formatDateOnly
    }),
    [
      dashboardData,
      loading,
      error,
      selectedPlatform,
      selectedAccount,
      getFilteredMetrics,
      selectPlatform,
      selectAccount,
      refreshData,
      getPlataformColor,
      formatCurrency,
      formatNumber
    ]
  )

  return <CampaignsContext.Provider value={value}>{children}</CampaignsContext.Provider>
}

export function useCampaigns() {
  const context = useContext(CampaignsContext)
  if (context === undefined) {
    throw new Error('useCampaigns must be used within a CampaignsProvider')
  }
  return context
}
